import { monthName } from '../lib/i18n/format'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@heroui/react'
import { api } from '../lib/api'
import { accepts } from '../lib/filePicker'
import { explain } from '../lib/staffErrors'
import { formatMoment, hasPhrase, type StaffKey, type StaffLanguage } from '../lib/staffText'
import type { LicenceState, LicenceStatus } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import { useFlash } from './sectionKit'
import { Notice, PageHead, SettingRow, SettingsCard, Values } from './settingsKit'
import '../styles/orgset.css'
import '../styles/install.css'

/**
 * The licence.
 *
 * This screen exists for two moments. The first is ordinary: somebody checking what
 * they bought against what they are using, once a quarter. The second is the one
 * worth designing for — something has gone wrong, configuration is refusing to
 * save, and the person looking at this screen needs to know in one sentence why and
 * what to do. So the state is stated in plain words before any of the figures, and
 * the installation code is right there to be copied into an email.
 *
 * What it never says is that the queue has stopped, because the queue has not
 * stopped. Every state below leaves customers being served.
 */

type Tone = 'ok' | 'warn'
// What the installation owner is told: in English, as the owner's screens are, and
// with the action that only the owner can take. An administrator reading the licence
// is told the state in their own language and that the owner can fix it.
const STATES: Record<LicenceState, { tone: Tone; says: string }> = {
  valid: { tone: 'ok', says: '' },
  unlicensed: { tone: 'warn', says: 'Everything works. Send us the code for a licence.' },
  // Nine times in ten a licence from the future is a server clock in the past.
  not_yet_valid: { tone: 'warn', says: 'Setup is locked. Check the server’s clock first.' },
  expired: { tone: 'warn', says: 'Setup is locked; customers are still served. Apply the renewal.' },
  foreign: { tone: 'warn', says: 'Setup is locked; customers are still served. Send us the code.' },
  needs_reactivation: { tone: 'warn', says: 'Setup is locked; customers are still served. Send us the code.' },
  // The licence is genuine: asking for a new file would produce the same one.
  needs_newer_server: { tone: 'warn', says: 'Setup is locked until this server is upgraded. The licence is fine.' },
}


/** 31 January 2027, for the owner's English lines. */
function longDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${d.getDate()} ${monthName('en', d.getMonth())} ${d.getFullYear()}`
}

/** A date with its year, in the reader's language. */
function dated(iso: string, lang: StaffLanguage): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : `${formatMoment(d, lang, { date: true })} ${d.getFullYear()}`
}

/** Whole days from today to the date; negative once it has passed. */
function daysFromToday(iso: string): number {
  const d = new Date(iso)
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime()
  return Math.round((start(d) - start(new Date())) / 86400000)
}

function inDays(n: number, say: Say): string {
  if (n === 0) return say('admin.licence.today')
  return n > 0 ? say('admin.licence.in', { n }) : say('admin.licence.ago', { n: -n })
}

/** A module's name in the reader's language, falling back to the server's label. */
function moduleName(name: string, label: string, say: Say): string {
  for (const key of [`org.br.module.${name}`, `admin.licence.module.${name}`]) {
    if (hasPhrase(key)) return say(key as StaffKey)
  }
  return label
}

type Say = (key: StaffKey, vars?: Record<string, string | number>) => string

/**
 * manage is licence.manage: the installation owner, who applies files and sees the
 * installation code. Without it the screen is the licence as an organisation's
 * administrator reads it (licence.read) — what was bought and what is used.
 */
export function Licence({ manage = true }: { manage?: boolean }) {
  const { lang, say } = useStaffText()
  const [status, setStatus] = useState<LicenceStatus | null>(null)
  const [loadError, setLoadError] = useState('')
  const [applyError, setApplyError] = useState('')
  const [applying, setApplying] = useState(false)
  const picker = useRef<HTMLInputElement>(null)
  const [copied, setCopied] = useState(false)
  const { flash, toast } = useFlash()

  const load = useCallback(async () => {
    setStatus(await api.licence())
  }, [])

  useEffect(() => {
    load().catch(() => setLoadError('The licence could not be read.'))
  }, [load])

  async function apply(file: File) {
    setApplyError('')
    // The dialog's "All files" ignores the filter; the server checks again anyway.
    if (!accepts('.qlic,application/json', file)) {
      setApplyError(`${file.name} is not a .qlic file.`)
      return
    }
    setApplying(true)
    try {
      setStatus(await api.applyLicence(file))
      flash('Licence applied')
    } catch (err) {
      setApplyError(explain(err, 'en', 'common.unreachable'))
    } finally {
      setApplying(false)
    }
  }

  async function copyInstallId() {
    try {
      await navigator.clipboard.writeText(status!.installId)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard access is refused in plenty of ordinary situations — an insecure
      // origin, a browser policy. The code is on screen and selectable, so this is
      // a convenience failing rather than the screen failing.
      setCopied(false)
    }
  }

  const head = (actions?: ReactNode) => <PageHead title={say('admin.section.licence')} line={say('settings.licence.line')} actions={actions} />
  if (!status)
    return (
      <>
        {head()}
        {loadError && <Notice>{loadError}</Notice>}
      </>
    )

  const state = STATES[status.state] ?? STATES.unlicensed
  const label = say(`admin.licence.state.${status.state in STATES ? status.state : 'unlicensed'}` as StaffKey)
  // An edge shows its server's licence: nothing to apply, no code, no usage here.
  const own = manage && !status.fromServer
  const says = own
    ? state.says
    : state.tone === 'ok'
      ? ''
      : status.state === 'unlicensed'
        ? say('admin.licence.saysWorks')
        : say('admin.licence.saysLocked')
  const doc = status.licence

  return (
    <>
      {head(
        own && (
          <>
            <input
              ref={picker}
              type="file"
              accept=".qlic,application/json"
              className="sr"
              tabIndex={-1}
              aria-hidden="true"
              disabled={applying}
              onChange={(e) => {
                const f = e.target.files?.[0]
                // Emptied whatever happens, so that choosing the same file again is a change.
                e.target.value = ''
                if (f) void apply(f)
              }}
            />
            <Button variant="primary" isDisabled={applying} onPress={() => picker.current?.click()}>
              <Icon name="upload" size={16} />
              {/* Checked here, against a key built into the software; nothing is sent anywhere. */}
              {applying ? say('settings.licence.checking') : say(doc ? 'settings.licence.replace' : 'settings.licence.apply')}
            </Button>
          </>
        ),
      )}
      {toast}
      {applyError && <Notice className="os-gap">{applyError}</Notice>}

      <SettingsCard title={say('settings.licence.status')}>
        <SettingRow
          label={
            <>
              <span className="lic-dot" data-tone={state.tone} aria-hidden="true" />
              {label}
            </>
          }
          status={
            says || status.fromServer ? (
              <>
                {says}
                {says && status.fromServer && ' '}
                {status.fromServer && say('admin.licence.fromServer')}
              </>
            ) : undefined
          }
        >
          {doc && (
            <Values items={[{ label: say('settings.licence.holder'), value: say('admin.licence.who', { customer: doc.customer.name, id: doc.licenceId }) }]} />
          )}
        </SettingRow>
        {own && (
          <SettingRow label={say('settings.licence.code')} description={say('settings.licence.codeLine')} control>
            <code className="lic-code">{status.installId}</code>
            <Button variant="ghost" onPress={() => void copyInstallId()}>
              <Icon name={copied ? 'check' : 'copy'} size={16} /> {say(copied ? 'settings.licence.copied' : 'settings.licence.copy')}
            </Button>
          </SettingRow>
        )}
        {doc && (
          <SettingRow label={say('settings.licence.file')}>
            <span className="lic-fine">
              {own ? (
                <>
                  {status.appliedAt && (
                    <>
                      Applied {longDate(status.appliedAt)}
                      {status.appliedBy ? ` by ${status.appliedBy}` : ''}.{' '}
                    </>
                  )}
                  Issued {longDate(doc.issuedAt)}.
                </>
              ) : (
                say('admin.licence.issued', { date: dated(doc.issuedAt, lang) })
              )}
            </span>
          </SettingRow>
        )}
      </SettingsCard>

      {doc && <Dates status={status} say={say} lang={lang} />}
      {doc && !status.fromServer && <Usage status={status} say={say} />}
      {doc && <Modules status={status} say={say} />}
    </>
  )
}

function Dates({ status, say, lang }: { status: LicenceStatus; say: Say; lang: StaffLanguage }) {
  const doc = status.licence!
  const useLeft = doc.model === 'subscription' && doc.notAfter ? daysFromToday(doc.notAfter) : null
  const m = doc.maintenanceUntil
  const mLeft = m ? daysFromToday(m) : null
  const mTotal = m ? daysFromToday(m) - daysFromToday(doc.notBefore || doc.issuedAt) : 0
  const mUsed = mLeft !== null && mTotal > 0 ? Math.min(1, Math.max(0, (mTotal - mLeft) / mTotal)) : 1
  const mSoon = mLeft !== null && mLeft >= 0 && mLeft <= 60
  const mWarn = status.maintenanceExpired || (mLeft !== null && mLeft < 0) || mSoon

  return (
    <SettingsCard title={say('admin.licence.dates')}>
      <SettingRow
        label={say('admin.licence.rightToUse')}
        status={say(doc.model === 'perpetual' ? 'admin.licence.perpetual' : 'admin.licence.yearly')}
      >
        <span className="lic-when">
          {useLeft === null ? (
            <strong>{say('admin.licence.neverEnds')}</strong>
          ) : (
            <>
              <strong data-tone={useLeft < 0 ? 'warn' : undefined}>
                {say(useLeft < 0 ? 'admin.licence.ended' : 'admin.licence.until', { date: dated(doc.notAfter!, lang) })}
              </strong>
              <span>{inDays(useLeft, say)}</span>
            </>
          )}
        </span>
      </SettingRow>
      <SettingRow label={say('admin.licence.upgrades')} description={say('admin.licence.upgradesHint')}>
        <span className="lic-when">
          {m && mLeft !== null ? (
            <>
              <strong data-tone={mWarn ? 'warn' : undefined}>
                {say(mLeft < 0 || status.maintenanceExpired ? 'admin.licence.ended' : 'admin.licence.until', { date: dated(m, lang) })}
              </strong>
              <span>{inDays(mLeft, say)}</span>
              <Meter share={mUsed} warn={mWarn} />
            </>
          ) : (
            <strong>{say('admin.licence.notCovered')}</strong>
          )}
        </span>
      </SettingRow>
    </SettingsCard>
  )
}

/** A thin bar: how much of a limit or a period is used. */
function Meter({ share, warn }: { share: number; warn?: boolean }) {
  return (
    <span className="os-bar-meter lic-meter" aria-hidden="true">
      <span className="os-bar-meter__fill" data-tone={warn ? 'warn' : undefined} style={{ width: `${Math.min(100, share * 100)}%` }} />
    </span>
  )
}

const COUNTED: Record<string, StaffKey> = {
  staff: 'admin.licence.staff',
  branches: 'admin.licence.branches',
  tenants: 'admin.licence.tenants',
  devices: 'admin.licence.devices',
}

function Usage({ status, say }: { status: LicenceStatus; say: Say }) {
  const doc = status.licence!
  const counted = status.counted ?? []
  const soft = doc.limits.devicesSoft
  const overSoft = status.overSoftLimits ?? []
  const named = (name: string) => (COUNTED[name] ? say(COUNTED[name]!) : name)
  const devicesOver = soft > 0 && status.devices > soft

  return (
    <SettingsCard title={say('admin.licence.usage')} line={say('admin.licence.across')}>
      {counted.map((c) => {
        const left = c.allowed - c.used
        const tight = c.allowed > 0 && left <= Math.max(1, Math.round(c.allowed * 0.05))
        return (
          <SettingRow key={c.name} label={named(c.name)} description={say('admin.licence.hardLimits')}>
            <span className="lic-use">
              {/* A limit of zero is no limit, and says so: "12 of 0" would read as an installation already over. */}
              <strong>
                {c.allowed > 0 ? say('admin.licence.of', { used: c.used, allowed: c.allowed }) : say('admin.licence.noLimit', { used: c.used })}
              </strong>
              <span className="lic-use__left" data-tone={tight ? 'warn' : undefined}>
                {c.allowed <= 0 ? '' : left <= 0 ? say('admin.licence.noneLeft') : say('admin.licence.left', { n: left })}
              </span>
              {c.allowed > 0 && <Meter share={c.used / c.allowed} warn={tight} />}
            </span>
          </SettingRow>
        )
      })}
      <SettingRow
        label={say('admin.licence.devices')}
        status={overSoft.length > 0 ? say('admin.licence.overSoft', { what: overSoft.map(named).join(', ') }) : undefined}
      >
        <span className="lic-use">
          <strong>{soft > 0 ? say('admin.licence.of', { used: status.devices, allowed: soft }) : status.devices}</strong>
          <span className="lic-use__left" data-tone={devicesOver ? 'warn' : undefined}>
            {devicesOver ? say('admin.licence.overNotBlocked') : ''}
          </span>
          {soft > 0 && <Meter share={status.devices / soft} warn={devicesOver} />}
        </span>
      </SettingRow>
    </SettingsCard>
  )
}

/**
 * Every delivered module, not only the ones that were bought. A list of what is on
 * cannot answer "where is Analytics?", and that is the question somebody opens this
 * screen with. A module this version has not built yet is never in the list: it is
 * named separately below it.
 */
function Modules({ status, say }: { status: LicenceStatus; say: Say }) {
  const pending = status.pendingModules ?? []
  const unknown = status.unknownModules ?? []
  return (
    <SettingsCard title={say('admin.licence.modules')}>
      <div className="lic-mods">
        <ul className="os-mods">
          {(status.modules ?? []).map((m) => (
            <li key={m.name} className="os-mod" data-off={!m.bought || undefined}>
              <span className="os-mod__mark" aria-hidden="true">
                {m.bought ? <Icon name="check" size={16} /> : null}
              </span>
              <strong>{moduleName(m.name, m.label, say)}</strong>
              <span className="os-mod__state">
                {!m.bought
                  ? say('admin.licence.notBought')
                  : m.kind === 'platform' || (status.fromServer && !m.every)
                    ? say('admin.licence.bought')
                    : m.every
                      ? say('admin.licence.everyBranch')
                      : say('admin.licence.seats', { used: m.used ?? 0, seats: m.seats ?? 0 })}
              </span>
            </li>
          ))}
        </ul>
        {pending.length > 0 && <p className="lic-fine">{say('admin.licence.pending', { names: pending.join(', ') })}</p>}
        {/* A licence issued for a later version: those modules appear when this server is upgraded. */}
        {unknown.length > 0 && <p className="lic-fine">{say('admin.licence.unknown', { names: unknown.join(', ') })}</p>}
      </div>
    </SettingsCard>
  )
}
